/**
 * The one place that turns a listing title into a /view-post slug.
 *
 * /view-post/:serviceName does NOT look a listing up by title — ViewAds posts
 * the slug to /api/serviceslinklist to translate it into a servicesID, and that
 * mapping only holds slugs built by THIS rule (it is the same sanitization the
 * post-creation path in services/UsePostEditPages.js writes into the
 * `serviceslink` collection, and that slugFromName() in modules/services.js
 * mirrors on the server).
 *
 * Every share/navigate helper used to fall back to a bare
 * `servicesName.toLowerCase().trim().replace(/\s+/g, "-")` whenever its
 * /api/serviceslinklist lookup failed. That keeps the commas, dots, slashes and
 * emoji the real rule strips, so it minted a slug matching no link record and
 * the listing page came up empty — "✨ Tomorrow, We Go Live! ✨" was shared as
 * `✨-tomorrow,-we-go-live!-✨` when the listing lives at `tomorrow-we-go-live`.
 * 443 of the ~3400 active listings have a title where the two rules disagree.
 *
 * Two rules that matter when calling this:
 *  - Only ever pass a listing TITLE. A slug that came back from
 *    /api/serviceslinklist is already canonical and must be used verbatim:
 *    duplicate titles are disambiguated with a "+1"/"+2" suffix, and "+" is not
 *    a \w character, so running a stored slug through here would silently turn
 *    `testing+5` into `testing5` and lose the listing.
 *  - A title cannot identify WHICH of several same-titled listings was meant,
 *    so a derived slug is a best effort, not a guarantee. Prefer the stored slug
 *    whenever one is available.
 */
export function canonicalSlug(name) {
  const slug = String(name || "")
    .toLowerCase()
    .trim()
    // Drop everything the link record wouldn't have kept: punctuation, emoji,
    // pipes, slashes. Spaces and dashes survive to the next step.
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    // A title that opens or closes with punctuation (an emoji, a "!") leaves a
    // dangling dash once that punctuation is gone. Stored slugs don't carry
    // one, so neither should a derived slug.
    .replace(/^-+|-+$/g, "");

  // No-op for the characters that survive the rules above; kept so this stays
  // byte-identical to the slug the create path stores.
  return encodeURIComponent(slug);
}

/**
 * Slug for a listing, preferring the canonical one the API returned.
 * `storedSlug` is used verbatim when present — see the "+N" note above.
 */
export function postSlugFor(storedSlug, servicesName) {
  return storedSlug || canonicalSlug(servicesName);
}
